import SparkMD5 from "spark-md5";

/**
 * Calculates the MD5 hash of a file and returns it as a base64-encoded string.
 * This hash is used for AWS S3 presigned URL content verification to ensure
 * the uploaded file matches the expected content.
 *
 * Uses a chunked approach to handle large files without loading them entirely into memory.
 *
 * @param file - The file to hash
 * @returns Promise resolving to base64-encoded MD5 hash
 * @throws Error if file reading or hashing fails
 */
export async function calculateFileMD5Base64(file: File): Promise<string> {
	try {
		return new Promise<string>((resolve, reject) => {
			const chunkSize = 2097152; // 2MB chunks for optimal performance
			const chunks = Math.ceil(file.size / chunkSize);
			let currentChunk = 0;
			const spark = new SparkMD5.ArrayBuffer();
			const fileReader = new FileReader();

			fileReader.onload = (event) => {
				if (!event.target?.result) {
					reject(new Error("Failed to read file chunk"));
					return;
				}

				spark.append(event.target.result as ArrayBuffer);
				currentChunk++;

				if (currentChunk < chunks) {
					loadNextChunk();
				} else {
					// Convert MD5 hex to base64 for AWS S3
					const md5Hex = spark.end();
					const contentMd5Hash = hexToBase64(md5Hex);
					resolve(contentMd5Hash);
				}
			};

			fileReader.onerror = () => {
				reject(new Error(`Failed to read file: ${fileReader.error?.message || "Unknown error"}`));
			};

			function loadNextChunk() {
				const start = currentChunk * chunkSize;
				const end = Math.min(start + chunkSize, file.size);
				fileReader.readAsArrayBuffer(file.slice(start, end));
			}

			loadNextChunk();
		});
	} catch (error) {
		throw new Error(
			`MD5 hash calculation failed: ${error instanceof Error ? error.message : "Unknown error"}`,
		);
	}
}

/**
 * Converts a hex string to base64.
 * Used to convert MD5 hex hash to base64 format required by AWS S3.
 *
 * @param hexString - Hexadecimal string to convert
 * @returns Base64-encoded string
 */
function hexToBase64(hexString: string): string {
	const bytes: number[] = [];
	for (let i = 0; i < hexString.length; i += 2) {
		bytes.push(parseInt(hexString.substr(i, 2), 16));
	}
	return btoa(String.fromCharCode(...bytes));
}
